/**
 * Output: the browser, with a line about the open study above it.
 *
 * This was a summary page whose "browse" was a small text link, so the browser
 * was findable once and then not again — and the summary it led with answered
 * a question ("how much did this produce") that the numbers on the project
 * page and the Results tab already answer.
 *
 * The hierarchy on disk runs project, output folder, subject, stage, file, and
 * that is a good hierarchy. So the screen is that tree, opened at the top by
 * project: a reader looking for last month's figures walks down to them rather
 * than being asked which root they mean.
 */

import { useEffect, useMemo } from "react";
import { ArrowRight, RefreshCw, TriangleAlert } from "lucide-react";

import { Button } from "@/components/ui/primitives";
import type { RecentCase } from "@/ipc/contract";
import { goToTab } from "@/pipeline/actions";
import { OutputBrowser, type OutputSource } from "@/outputs/OutputBrowser";
import { useProjects } from "@/projects/store";
import { useResultsStore } from "@/results/state";
import { appActions, useAppStore } from "@/store/appStore";
import { nodeAbsPath, useViewerStore } from "@/viewer/viewerStore";
import { Eyebrow } from "./home.design";
import { groupByOutputRoot } from "./outputs";

function ml(value: number | null | undefined): string {
  return value == null ? "—" : `${value.toFixed(2)} mL`;
}

export function OutputPane({
  recent,
  onRefresh,
}: {
  recent: RecentCase[];
  onRefresh: () => void;
}) {
  const manifest = useAppStore((s) => s.currentCase);
  const summary = useResultsStore((s) => s.summary);
  const loadSummary = useResultsStore((s) => s.loadSummary);
  const projects = useProjects((s) => s.items);
  const refreshProjects = useProjects((s) => s.refresh);

  useEffect(() => {
    if (manifest) void loadSummary();
  }, [manifest, loadSummary]);
  useEffect(() => {
    void refreshProjects();
  }, [refreshProjects]);

  /**
   * Send a volume to the viewer, when it belongs to the study that is open.
   *
   * The viewer reads nodes of the open case, and this tree spans folders
   * holding many subjects — so a file from another study has no node to
   * select. Rather than appearing to work and showing the wrong image, that
   * case says which study the file belongs to.
   */
  const openVolume = (path: string) => {
    const open = useAppStore.getState().currentCase;
    const node = open
      ? [...open.images, ...open.masks, ...open.atlases].find((n) => nodeAbsPath(open, n) === path)
      : null;
    if (node) {
      useViewerStore.getState().setBaseNode(node.id);
      goToTab("view");
      return;
    }
    appActions.notify({
      title: "That volume belongs to another study",
      detail:
        "Open it as a study and the viewer will show it. It can still be opened outside the app from here.",
      tone: "info",
    });
  };

  /**
   * The top of the tree: projects first, each under its own name, then the
   * output folders that belong to no project. A folder a project already
   * covers is not listed twice.
   */
  const sources = useMemo((): OutputSource[] => {
    const out: OutputSource[] = projects.map((p) => ({
      id: `project:${p.id}`,
      label: p.name,
      sublabel: p.outputRoot,
      path: p.outputRoot,
      kind: "project",
    }));
    const claimed = new Set(projects.map((p) => p.outputRoot));

    for (const group of groupByOutputRoot(recent)) {
      if (!group.root || claimed.has(group.root)) continue;
      claimed.add(group.root);
      out.push({
        id: `root:${group.root}`,
        label: group.root.split(/[\\/]/).filter(Boolean).pop() ?? group.root,
        sublabel: group.root,
        path: group.root,
        kind: "root",
      });
    }
    return out;
  }, [projects, recent]);

  const missing = recent.filter((c) => !c.exists).length;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-3 border-b border-ads-border bg-ads-surface px-4 py-2">
        <Eyebrow>Output</Eyebrow>
        {manifest ? (
          <>
            <span className="shrink-0 text-[12px] font-medium text-neutral-800">
              {manifest.subjectId}
            </span>
            {summary?.coreVolumeMl != null ? (
              // Both spaces, because only one of them is the patient's.
              <span className="truncate text-[11px] tabular-nums text-neutral-500">
                core {ml(summary.coreVolumeMl)} {summary.coreVolumeSpace} · native{" "}
                {ml(summary.coreVolumeNativeMl)}
              </span>
            ) : null}
            <Button size="sm" variant="outline" onClick={() => goToTab("results")}>
              Open results
              <ArrowRight className="h-3 w-3" />
            </Button>
          </>
        ) : (
          <span className="text-[11.5px] text-neutral-500">
            Everything your runs produced, by project and folder.
          </span>
        )}
        <span className="flex-1" />
        {missing ? (
          <span className="flex shrink-0 items-center gap-1.5 text-[10.5px] text-amber-700">
            <TriangleAlert className="h-3 w-3" />
            {missing} folder{missing === 1 ? "" : "s"} no longer on disk
          </span>
        ) : null}
        <Button size="sm" variant="outline" className="shrink-0" onClick={onRefresh}>
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </Button>
      </div>

      <div className="min-h-0 flex-1">
        <OutputBrowser sources={sources} onOpenVolume={openVolume} />
      </div>
    </div>
  );
}

export default OutputPane;
